// =============
// Orders Module
// =============

import axios from '@/axios';
import ReviewComment from '@/models/ReviewComment';
import ReviewRequest from '@/models/ReviewRequest';

export default
{
    namespaced: true,


    // ------------------------------------------------------------ STATE

    state()
    {
        return {
            set:   [],
            total: 0,

            waitingForReview:                [],
            waitingForReviewRefreshInterval: 0,
            fetchingWaitingForReview:        false,

            lastCommentDatesByOrder: new Map(),
        };
    },


    // ------------------------------------------------------------ GETTERS

    getters:
    {
        getWaitingForReviewOrderByLensPairId: state => lensPairId =>
        {
            // Build other side lens pair ID.
            const letter = lensPairId.endsWith('A') ? 'B' : 'A';
            const otherSideLensPairId = lensPairId.slice(0, -1) + letter;

            // Look for other side lens.
            for(const i in state.waitingForReview)
            {
                const order = state.waitingForReview[i];
                if(order.article_data.LENS_PAIR_ID === otherSideLensPairId)
                {
                    return order;
                }
            }

            return null;
        },
    },


    // ------------------------------------------------------------ MUTATIONS

    mutations:
    {
        setOrderSet(state, orders)
        {
            state.set = orders;
        },

        setTotal(state, total)
        {
            state.total = total;
        },

        setWaitingForReview(state, orders)
        {
            state.waitingForReview = orders;
        },

        setLastCommentDatesByOrder(state, dates)
        {
            state.lastCommentDatesByOrder = dates;
        },

        setWaitingForReviewRefreshInterval(state, intervalId)
        {
            state.waitingForReviewRefreshInterval = intervalId;
        },

        setFetchingWaitingForReview(state, fetching)
        {
            state.fetchingWaitingForReview = !!fetching;
        },

        setDeletionRequested(state, { orderId, deletionRequested = '1' })
        {
            const order = state.set.find(o => o.id == orderId);
            if(!order)
            {
                return;
            }

            order.extra.deletion_requested = deletionRequested;
        },
    },


    // ------------------------------------------------------------ ACTIONS

    actions:
    {
        async fetchOrders({ commit, rootState }, { offset = -1, limit = -1 } = {})
        {
            if(offset === -1) offset = '';
            if(limit === -1) limit = '';

            const url = '/api/order/entity/:eid/:offset/:limit'
                .replace(':eid', rootState.account.cEntity.id)
                .replace(':offset', offset)
                .replace(':limit', limit);

            const { data: orders } = await axios.get(url);
            return orders;
        },

        searchOrders({ commit, rootState }, { offset, limit, query = '', filters = {} })
        {
            const url = '/api/order/search/entity/:eid/:offset/:limit'
                .replace(':eid', rootState.account.cEntity.id)
                .replace(':offset', offset)
                .replace(':limit', limit);

            return axios.post(url + '?q=' + encodeURIComponent(query), { filters })
                .then(({ data: orders, meta }) =>
                {
                    commit('setOrderSet', orders);
                    commit('setTotal', meta.total);

                    return orders;
                });
        },

        /**
         * Update given orders.
         */
        update({}, { orders, options })
        {
            return new Promise((resolve, reject) =>
            {
                axios.put('/api/order', { orders, options })
                    .then(response =>
                    {
                        resolve(response);
                    })
                    .catch(error =>
                    {
                        console.log(error);
                        reject(error);
                    });
            });
        },

        async fetchWaitingForReview(
            { commit, state },
            { onError = null, eid = '' } = {}
        )
        {
            onError ??= error =>
            {
                throw error;
            };

            if(state.fetchingWaitingForReview)
            {
                return;
            }

            commit('setFetchingWaitingForReview', true);
            try
            {
                const requests = await ReviewRequest.fetchActive(eid);
                commit('setWaitingForReview', requests);
                return requests;
            }
            catch(error)
            {
                console.error('could not fetch orders waiting for review', { error });
                onError(error);
            }
            finally
            {
                commit('setFetchingWaitingForReview', false);
            }
        },

        async startWaitingForReviewRefreshInterval(
            { commit, state, dispatch },
            { prefetch = true, onError = null, eid = '' } = {}
        )
        {
            // Ensure only one interval is running
            if(state.waitingForReviewRefreshInterval)
            {
                return state.waitingForReviewRefreshInterval;
            }

            const job = async() =>
            {
                // Fetch active review requests
                /** @type {ReviewRequest[]} */
                const requests = await dispatch('fetchWaitingForReview', { onError, eid });

                // Fetch last comment date for each order
                await dispatch('fetchLastCommentDates');
            };

            // Retrieve the orders before waiting for the first interval to run
            if(prefetch)
            {
                job();
            }

            // Refresh review requests every minute
            const intervalId = setInterval(job, 60_000);
            commit('setWaitingForReviewRefreshInterval', intervalId);

            return intervalId;
        },

        async stopWaitingForReviewRefreshInterval({ commit, state })
        {
            if(state.waitingForReviewRefreshInterval)
            {
                clearInterval(state.waitingForReviewRefreshInterval);
                commit('setWaitingForReviewRefreshInterval', 0);
            }
        },

        /**
         * Retrieve the date of the last comment of a bunch of orders.
         *
         * @param {*} param0
         * @returns {Record<string, number>} The timestamp of the last comment
         *                                   for each order, indexed by the
         *                                   order's reference.
         */
        async fetchLastCommentDates({ commit })
        {
            const dates = await ReviewComment.fetchLastDateForActive();
            commit('setLastCommentDatesByOrder', dates);
            return dates;
        },

        setDeletionRequested({ commit }, { orderId, deletionRequested = '1' })
        {
            return new Promise((resolve, reject) =>
            {
                try
                {
                    commit('setDeletionRequested', { orderId, deletionRequested });
                    resolve();
                }
                catch(e)
                {
                    reject(e);
                }
            });
        },
    },
};
